import { ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Skeleton } from 'primeng/skeleton';
import { Chip } from 'primeng/chip';
import {
  AppointmentsClient,
  ClientAppointmentTableResponse,
  CompaniesClient,
  CompanyDashboardResponse,
  GetClientAppointmentsQuery,
  GetPackagesForCompanyQuery,
  PackageFilter,
  PackagesClient,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { UserService } from '../../../infrastructure/shared/services/user.service';
import { LocationCompanyService } from '../../../infrastructure/shared/services/location-company.service';
import {
  convertDateToHhMm,
  getDateFormatedSrLong,
  getDayFullMonthSr,
  getMonthShortSr,
  getMonthYearSr,
} from '../../../infrastructure/shared/services/date.service';
import { Hourglas03 } from '../../../infrastructure/shared/icons/hourglas-03/hourglas-03';
import { List } from '../../../infrastructure/shared/icons/list/list';
import { Clock } from '../../../infrastructure/shared/icons/clock/clock';
import { ClockFastForward } from '../../../infrastructure/shared/icons/clock-fast-forward/clock-fast-forward';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { Calendar } from '../../../infrastructure/shared/icons/calendar/calendar';
import { ArrowUpRight } from '../../../infrastructure/shared/icons/arrow-up-right/arrow-up-right';
import { ChevronRight } from '../../../infrastructure/shared/icons/chevron-right/chevron-right';
import { Edit03 } from '../../../infrastructure/shared/icons/edit-03/edit-03';
import { SlashCircle01 } from '../../../infrastructure/shared/icons/slash-circle-01/slash-circle-01';
import { InfoCircle } from '../../../infrastructure/shared/icons/info-circle/info-circle';
import { XClose } from '../../../infrastructure/shared/icons/x-close/x-close';
import { ClientAppointmentRescheduleDialog } from '../../../infrastructure/shared/components/client-appointment-reschedule-dialog/client-appointment-reschedule-dialog';
import { ClientAppointmentCancelDialog } from '../../../infrastructure/shared/components/client-appointment-cancel-dialog/client-appointment-cancel-dialog';
import { ClientAppointmentDetailsDrawer } from '../../../infrastructure/shared/components/client-appointment-details-drawer/client-appointment-details-drawer';

@Component({
  standalone: true,
  selector: 'app-company-dashboard',
  imports: [
    Skeleton,
    Chip,
    ButtonComponent,
    Hourglas03,
    List,
    Clock,
    ClockFastForward,
    Cube01,
    Calendar,
    ArrowUpRight,
    ChevronRight,
    Edit03,
    SlashCircle01,
    InfoCircle,
    XClose,
    ClientAppointmentRescheduleDialog,
    ClientAppointmentCancelDialog,
    ClientAppointmentDetailsDrawer,
  ],
  templateUrl: './company-dashboard.html',
  styleUrl: './company-dashboard.scss',
})
export class CompanyDashboard {
  private _companiesClient = inject(CompaniesClient);
  private _appointmentsClient = inject(AppointmentsClient);
  private _packagesClient = inject(PackagesClient);
  private _user = inject(UserService);
  private _location = inject(LocationCompanyService);
  private _cdr = inject(ChangeDetectorRef);

  protected dash = signal<CompanyDashboardResponse | undefined>(undefined);
  protected dashLoading = signal(true);

  protected appointments = signal<ClientAppointmentTableResponse[]>([]);
  protected apptsLoading = signal(true);

  protected pendingCount = signal<number>(0);

  protected bannerDismissed = signal(false);

  /** Reschedule / cancel / details dialogs */
  protected showRescheduleDialog = signal(false);
  protected selectedRescheduleId = signal<string | undefined>(undefined);
  protected showCancelDialog = signal(false);
  protected selectedCancelId = signal<string | undefined>(undefined);
  protected showDetailsDrawer = signal(false);
  protected selectedDetailsId = signal<string | undefined>(undefined);

  protected userId = this._user.getUserData.id;

  protected greetingName = computed(() => {
    return 'korisniče';
    // const full = this._user.getUserData.fullName ?? '';
    // return full.trim().split(/\s+/)[0] ?? '';
  });

  protected todayLabel = computed(() => getDateFormatedSrLong(new Date()).toUpperCase());
  protected currentMonthLabel = computed(() => getMonthYearSr(new Date()));

  protected canBook = computed(() => (this.dash()?.availableHours ?? 0) > 0);

  protected showProcessingBanner = computed(() => this.pendingCount() > 0);

  protected showBanner = computed(
    () =>
      !this.dashLoading() &&
      !this.bannerDismissed() &&
      !this.canBook() &&
      this.pendingCount() === 0,
  );

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  constructor() {
    this._loadDashboard();
    this._loadAppointments();
    this._loadPendingCount();
  }

  protected dismissBanner(): void {
    this.bannerDismissed.set(true);
  }

  protected packageCountLabel(count: number | undefined): string {
    const n = count ?? 0;
    return n === 1 ? '1 aktivni paket' : `${n} aktivnih paketa`;
  }

  protected daysUntilLabel(days: number | undefined | null): string {
    if (days === undefined || days === null) return '-';
    if (days === 0) return 'Danas';
    return String(days);
  }

  protected daysUntilUnit(days: number | undefined | null): string {
    if (days === undefined || days === null || days === 0) return '';
    return days === 1 ? 'dan' : 'dana';
  }

  protected nextAppointmentDateLabel(date: Date | undefined): string {
    if (!date) return 'Nemate zakazan termin';
    return getDayFullMonthSr(date);
  }

  protected getMonthLabel(date?: Date): string {
    if (!date) return '';
    return getMonthShortSr(date);
  }

  protected getDayLabel(date?: Date): string {
    if (!date) return '';
    return String(date.getDate()).padStart(2, '0');
  }

  protected formatTimeRange(from?: Date, to?: Date): string {
    if (!from || !to) return '';
    return `${convertDateToHhMm(from)} - ${convertDateToHhMm(to)}`;
  }

  protected goToPackages(): void {
    this._location.routeToPackages();
  }

  protected goToOffer(): void {
    this._location.routeToPackagesOffer();
  }

  protected goToAppointments(): void {
    this._location.routeToAppointments();
  }

  protected goToCalendar(): void {
    this._location.routeToCalendar();
  }

  protected goToCreateAppointment(): void {
    if (!this.canBook()) return;
    this._location.routeToCreateAppointment();
  }

  protected openDetails(id: string): void {
    this.selectedDetailsId.set(id);
    this.showDetailsDrawer.set(true);
  }

  protected openReschedule(id: string, event: Event): void {
    event.stopPropagation();
    this.selectedRescheduleId.set(id);
    this.showRescheduleDialog.set(true);
  }

  protected openCancel(id: string, event: Event): void {
    event.stopPropagation();
    this.selectedCancelId.set(id);
    this.showCancelDialog.set(true);
  }

  protected onChanged(): void {
    this._loadDashboard();
    this._loadAppointments();
  }

  private async _loadDashboard(): Promise<void> {
    this.dashLoading.set(true);
    const res = await firstValueFrom(this._companiesClient.getCompanyDashboard());
    this.dash.set(res.result);
    this.dashLoading.set(false);
    this._cdr.markForCheck();
  }

  private async _loadPendingCount(): Promise<void> {
    const res = await firstValueFrom(
      this._packagesClient.getPackagesForCompany({
        filter: {
          statuses: [UserPackageStatus.NEW, UserPackageStatus.INVOICE_SENT],
        } as PackageFilter,
        pageNumber: 1,
        pageSize: 1,
      } as GetPackagesForCompanyQuery),
    );
    this.pendingCount.set(res.result.totalCount ?? 0);
    this._cdr.markForCheck();
  }

  private async _loadAppointments(): Promise<void> {
    this.apptsLoading.set(true);
    const res = await firstValueFrom(
      this._appointmentsClient.getClientAppointments({
        userId: this.userId,
        isUpcoming: true,
        pageNumber: 1,
        pageSize: 3,
      } as GetClientAppointmentsQuery),
    );
    this.appointments.set(res.result.data ?? []);
    this.apptsLoading.set(false);
    this._cdr.markForCheck();
  }
}
